import React, { useState, useEffect, useCallback } from 'react';
import _, { clone } from 'lodash';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import { Checkbox, Input, PrimaryButton, Switch } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { getFetcher } from '../../../../App';
import useStaticDialog from '../../../../utils/hooks/useStaticDialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import {
  StyledInputList,
  StyledSettingBox,
  StyledSettingList,
  StyledSettingWrapper,
  StyledReportWrap,
  StyledSwButtonSection,
  StyledSeriesList,
  StyledSmallTxt,
} from './style';
import { submitDebounce } from 'utils/lib';

interface sendToPacsState {
  [key: string]: any;
}
const SCSSendToPacs = () => {
  const { submitConfirmDialog, confirmTurnAutomaticallyOnOffDialog } = useStaticDialog();
  const { errorHandler } = SettingCommonMethods();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const [data, setData] = useState<sendToPacsState>({
    sendResult: true,
    sendItemResultImage: true,
    seriesCompositionType: 'INDIVIDUAL',
    sendItemReport: true,
  });
  const [backupData, setBackupData] = useState<sendToPacsState>({
    sendResult: true,
    sendItemResultImage: true,
    seriesCompositionType: 'INDIVIDUAL',
    sendItemReport: true,
  });

  /**
   * 제품별 각각 다 들어갈 수 있는 모듈로 제품에 따라
   * api 경로 다르게 반환 하기 위한 함수
   */
  const getAPIUrl = () => {
    return `api/setting/scs/pacs`;
  };

  /**
   * 사용자 가 값 변경시 동작
   * @param key
   * @param value
   */

  const setDataValue = (key: string, value: boolean) => {
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });
  };

  /**
   * 스위치 변경 컨펌
   * @param key
   * @param value
   */
  const confirmTurnOnOff = (key: string, value: boolean) => {
    const turn = value ? 'on' : 'off';

    //먼저 바꾸고 컨펌에 따라 결과 확정
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });

    confirmTurnAutomaticallyOnOffDialog(
      turn,
      () => null,
      () => {
        setDataValue(key, !value);
      },
    );
  };

  const sendItemsChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setData((prevState) => {
      if (!prevState) return null;

      return {
        ...prevState,
        [name]: checked,
      };
    });
  };

  /**
   * submit confirm cb
   * data 전송
   */
  const postDataCB = async () => {
    // valid 체크
    const url = getAPIUrl();
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: {
        ...data,
      },
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setBackupData(data);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * get send to pacs data
   */
  const getData = async () => {
    const url = getAPIUrl(); // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setData(r.result);
          setBackupData(r.result);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * series Config 변경 핸들러
   *
   */
  const seriesConfigHandler = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const modeValue = ev.target.value;
      const modeName = ev.target.name;

      setData((prevState) => {
        if (!prevState) return null;
        const cloneData = JSON.parse(JSON.stringify(prevState));
        cloneData.seriesCompositionType = backupData?.seriesCompositionType;
        cloneData[modeName] = modeValue;

        return cloneData;
      });
    },
    [backupData],
  );

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, []);

  return (
    <>
      <label>Send to PACS</label>
      <StyledReportWrap>
        <StyledSettingWrapper>
          <strong>Send automatically</strong>
          <StyledSettingBox>
            <StyledSettingList className="report">
              <li>
                <StyledInputList>
                  <span className={'send-off'}>Off</span>

                  <Switch defaultValue={data.sendResult} onChangeValue={(e) => confirmTurnOnOff('sendResult', e)} />
                  <span>On</span>
                </StyledInputList>
              </li>
            </StyledSettingList>
          </StyledSettingBox>
        </StyledSettingWrapper>

        <StyledSettingWrapper>
          <strong>Send items</strong>
          <StyledSettingBox>
            <StyledInputList>
              <li>
                <Checkbox
                  checked={data.sendItemResultImage}
                  name="sendItemResultImage"
                  onChange={(e) => sendItemsChecked(e)}
                >
                  Image
                </Checkbox>
              </li>
              <li>
                <Checkbox checked={data.sendItemReport} name="sendItemReport" onChange={(e) => sendItemsChecked(e)}>
                  Report
                  <StyledSmallTxt>(Do not send to PACS when set to FDA mode.)</StyledSmallTxt>
                </Checkbox>
              </li>
            </StyledInputList>
          </StyledSettingBox>
        </StyledSettingWrapper>

        <StyledSettingWrapper>
          <strong>Series config</strong>
          <StyledSettingBox>
            <StyledSeriesList>
              <li>
                <Input
                  type="radio"
                  name="seriesCompositionType"
                  value="INDIVIDUAL"
                  checked={data.seriesCompositionType === 'INDIVIDUAL'}
                  onChange={seriesConfigHandler}
                >
                  Individual
                </Input>
              </li>
              <li>
                <Input
                  type="radio"
                  name="seriesCompositionType"
                  value="INTEGRATION"
                  checked={data.seriesCompositionType === 'INTEGRATION'}
                  onChange={seriesConfigHandler}
                >
                  Integration
                </Input>
              </li>
            </StyledSeriesList>
          </StyledSettingBox>
        </StyledSettingWrapper>
      </StyledReportWrap>
      <StyledSwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={submitDebounce(() => submitConfirmDialog(postDataCB))}
          disabled={_.isEqual(data, backupData)}
        >
          Submit
        </PrimaryButton>
      </StyledSwButtonSection>
    </>
  );
};

export default SCSSendToPacs;
